<template>
  <div class="rental-page" :dir="language === 'he' ? 'rtl' : 'ltr'">
    <!-- Hero Section -->
    <div class="hero-section">
      <div class="hero-content">
        <h1 class="hero-title">{{ t('heroTitle') }}</h1>
        <p class="hero-subtitle">{{ t('heroSubtitle') }}</p>
      </div>
    </div>

    <!-- Main Form -->
    <div class="rental-form" v-if="!isCompleted">
      <!-- Language Selector -->
      <div class="lang-selector">
        <button
          class="lang-btn"
          :class="{ active: language === 'he' }"
          @click="setLanguage('he')"
        >עברית</button>
        <button
          class="lang-btn"
          :class="{ active: language === 'en' }"
          @click="setLanguage('en')"
        >English</button>
      </div>

      <!-- Progress Bar -->
      <div class="progress-bar" v-if="currentStep <= 3">
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: ((currentStep - 1) / 2) * 100 + '%' }"></div>
        </div>
        <div class="progress-steps">
          <div class="progress-dot" :class="{ active: currentStep >= 1, done: currentStep > 1 }">
            <span>1</span>
          </div>
          <div class="progress-dot" :class="{ active: currentStep >= 2, done: currentStep > 2 }">
            <span>2</span>
          </div>
          <div class="progress-dot" :class="{ active: currentStep >= 3, done: currentStep > 3 }">
            <span>3</span>
          </div>
        </div>
      </div>

      <!-- Payment iframe -->
      <div class="payment-frame" v-if="paymentUrl">
        <h2 class="payment-title">{{ t('goToPayment') }}</h2>
        <iframe ref="paymentFrame" :src="paymentUrl"></iframe>
        <a class="go-back" @click="paymentUrl = ''">{{ t('back') }}</a>
      </div>

      <template v-if="!paymentUrl">
      <!-- Step 1: Package + Date Selection -->
      <div class="step" :class="{
        'completed': currentStep > 1,
        'active': currentStep === 1
      }" v-if="currentStep >= 1">
        <div class="step-header">
          <div class="step-number">1</div>
          <h2>{{ t('step1Title') }}</h2>
          <p>{{ t('step1Subtitle') }}</p>
          <div class="edit-icon" v-if="currentStep > 1 && canEditStep1" @click="goToStep(1)">
            <font-awesome-icon icon="fa fa-edit" />
          </div>
        </div>

        <div class="package-options">
          <div
            v-for="pkg in packages"
            :key="pkg.id"
            class="package-card"
            :class="{ active: selectedPackage && selectedPackage.id === pkg.id }"
            @click="selectPackage(pkg)"
          >
            <div class="package-icon">{{ pkg.flag }}</div>
            <h3>{{ language === 'he' ? pkg.name : pkg.name_en }}</h3>
            <p>{{ language === 'he' ? pkg.description : pkg.description_en }}</p>
            <div class="package-price-range">
              {{ language === 'he' ? 'החל מ-' : 'From ' }}₪{{ Math.min(...pkg.periods.map(p => p.price)) }}
            </div>
          </div>
        </div>

        <div class="period-selection" v-if="selectedPackage">
          <div class="pricing-table">
            <div class="pricing-row" v-for="tier in pricingTiers" :key="tier.label">
              <span class="tier-label">{{ tier.label }}</span>
              <span class="tier-price">₪{{ tier.price }}</span>
            </div>
          </div>

          <div class="date-inputs">
            <div class="date-input-group">
              <label>{{ t('startDate') }}</label>
              <input
                type="date"
                v-model="rentalStartDate"
                :min="today"
                @change="calculateRentalPeriod"
                @input="calculateRentalPeriod"
                class="date-input"
              />
            </div>

            <div class="date-input-group">
              <label>{{ t('endDate') }}</label>
              <input
                type="date"
                v-model="rentalEndDate"
                :min="minEndDate"
                @change="calculateRentalPeriod"
                @input="calculateRentalPeriod"
                class="date-input"
                :class="{ 'error': dateError }"
              />
            </div>
          </div>

          <div class="min-period-note">
            <p>{{ t('minPeriodNote') }}</p>
          </div>

          <div class="date-error" v-if="dateError">
            <p>{{ dateError }}</p>
          </div>

          <div class="rental-period-display" v-if="rentalStartDate && rentalEndDate && !dateError">
            <div class="period-info">
              <div class="period-dates">
                <span class="date-label">{{ t('fromDate') }}</span>
                <span class="date-value">{{ formatDate(rentalStartDate) }}</span>
              </div>
              <div class="period-dates">
                <span class="date-label">{{ t('toDate') }}</span>
                <span class="date-value">{{ formatDate(rentalEndDate) }}</span>
              </div>
              <div class="period-duration">
                <span class="duration-label">{{ t('rentalDuration') }}</span>
                <span class="duration-value">{{ rentalDuration }} {{ language === 'he' ? 'ימים' : 'days' }}</span>
              </div>
            </div>
          </div>

          <div class="price-display" v-if="rentalStartDate && rentalEndDate && !dateError">
            <div class="price-item total">
              <span>{{ t('totalToPay') }}</span>
              <span class="price">₪{{ calculatedPrice }}</span>
            </div>
          </div>
        </div>

        <div class="step-actions" v-if="currentStep === 1 && selectedPackage">
          <button
            class="btn-primary"
            @click="nextStep"
            :disabled="!selectedPackage || !rentalStartDate || !rentalEndDate || !!dateError"
          >
            {{ t('continue') }}
          </button>
        </div>
      </div>

      <!-- Step 2: SIM + End-of-Period + Customer Details -->
      <div class="step" :class="{
        'completed': currentStep > 2,
        'active': currentStep === 2
      }" v-if="currentStep >= 2">
        <div class="step-header">
          <div class="step-number">2</div>
          <h2>{{ t('step2Title') }}</h2>
          <p>{{ t('step2Subtitle') }}</p>
          <div class="edit-icon" v-if="currentStep > 2 && canEditStep2" @click="goToStep(2)">
            <font-awesome-icon icon="fa fa-edit" />
          </div>
        </div>

        <div class="combined-details-section" v-if="selectedPackage">
          <div class="sim-input-form">
            <div class="form-group">
              <label>{{ t('simLabel') }}</label>
              <input
                type="tel"
                v-model="simNumber"
                @input="validateSimNumber"
                class="sim-input"
                :class="{ 'error': simNumberError }"
                placeholder="12345678"
              />
              <div class="sim-error" v-if="simNumberError">
                <p>{{ simNumberError }}</p>
              </div>
            </div>

            <div class="end-period-option">
              <label class="option-title">{{ t('endPeriodTitle') }}</label>
              <div class="radio-options">
                <label class="radio-label">
                  <input
                    type="radio"
                    v-model="endPeriodAction"
                    value="freeze"
                    class="radio-input"
                  />
                  <span class="radio-text">
                    {{ t('freezeText') }}
                    <span class="freeze-price">{{ t('freezePrice') }}</span>
                  </span>
                </label>
                <label class="radio-label">
                  <input
                    type="radio"
                    v-model="endPeriodAction"
                    value="cancel"
                    class="radio-input"
                  />
                  <span class="radio-text">
                    {{ t('cancelText') }}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div class="section-divider"></div>

          <div class="customer-form">
            <div class="form-row">
              <div class="form-group">
                <label>{{ t('firstName') }} <span class="required">*</span></label>
                <input
                  type="text"
                  v-model="customerDetails.firstName"
                  required
                />
              </div>
              <div class="form-group">
                <label>{{ t('lastName') }} <span class="required">*</span></label>
                <input
                  type="text"
                  v-model="customerDetails.lastName"
                  required
                />
              </div>
            </div>

            <div class="form-group">
              <label>{{ t('email') }} <span class="required">*</span></label>
              <input
                type="email"
                v-model="customerDetails.email"
                required
                :class="{ 'error': emailError }"
                @blur="validateEmail"
              />
              <span class="field-hint">{{ t('emailHint') }}</span>
              <div class="field-error" v-if="emailError">
                <p>{{ emailError }}</p>
              </div>
            </div>

            <div class="form-group">
              <label>{{ t('phone') }} <span class="required">*</span></label>
              <input
                type="tel"
                v-model="customerDetails.phone"
                required
              />
              <span class="field-hint">{{ t('phoneHint') }}</span>
            </div>
          </div>
        </div>

        <div class="step-actions" v-if="currentStep === 2">
          <button class="btn-secondary" @click="prevStep">{{ t('back') }}</button>
          <button
            class="btn-primary"
            @click="nextStep"
            :disabled="!simNumber || !!simNumberError || !endPeriodAction || !isCustomerFormValid"
          >
            {{ t('continue') }}
          </button>
        </div>
      </div>

      <!-- Step 3: Order Summary -->
      <div class="step" :class="{
        'completed': currentStep > 3,
        'active': currentStep === 3
      }" v-if="currentStep >= 3">
        <div class="step-header">
          <div class="step-number">3</div>
          <h2>{{ t('step3Title') }}</h2>
          <p>{{ t('step3Subtitle') }}</p>
        </div>

        <div class="package-summary" v-if="selectedPackage && rentalStartDate && rentalEndDate && !dateError && simNumber && !simNumberError">
          <div class="summary-card">
            <div class="summary-header">
              <div class="package-flag">{{ selectedPackage.flag }}</div>
              <div class="package-details">
                <h3>{{ language === 'he' ? selectedPackage.name : selectedPackage.name_en }}</h3>
                <p>{{ language === 'he' ? selectedPackage.description : selectedPackage.description_en }}</p>
              </div>
            </div>

            <div class="summary-content">
              <div class="summary-item">
                <span>{{ t('customerName') }}</span>
                <span>{{ customerDetails.firstName }} {{ customerDetails.lastName }}</span>
              </div>
              <div class="summary-item">
                <span>{{ t('email') }}</span>
                <span>{{ customerDetails.email }}</span>
              </div>
              <div class="summary-item">
                <span>{{ t('phone') }}</span>
                <span>{{ customerDetails.phone }}</span>
              </div>
              <div class="summary-item">
                <span>{{ t('simNumber') }}</span>
                <span>{{ simNumber }}</span>
              </div>
              <div class="summary-item">
                <span>{{ t('rentalPeriod') }}</span>
                <span>{{ rentalDuration }} {{ language === 'he' ? 'ימים' : 'days' }} ({{ formatDate(rentalStartDate) }} - {{ formatDate(rentalEndDate) }})</span>
              </div>
              <div class="summary-item">
                <span>{{ t('endPeriodAction') }}</span>
                <span>{{ endPeriodAction === 'freeze' ? t('freezeLabel') : t('cancelLabel') }}</span>
              </div>
              <div class="summary-item freeze-info" v-if="endPeriodAction === 'freeze'">
                <span>{{ t('freezeCostAfter') }}</span>
                <span>₪{{ freezeCost }} / {{ t('perMonth') }}</span>
              </div>
              <div class="summary-item total">
                <span>{{ t('totalToPay') }}</span>
                <span class="price">₪{{ calculatedPrice }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="sim-error payment-error" v-if="currentStep === 3 && paymentError">
          <p>{{ paymentError }}</p>
        </div>

        <div class="step-actions" v-if="currentStep === 3">
          <button class="btn-secondary" @click="prevStep">{{ t('back') }}</button>
          <button
            class="btn-primary"
            @click="processPayment"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing">{{ t('processing') }}</span>
            <span v-else>{{ t('goToPayment') }}</span>
          </button>
        </div>
      </div>

      <!-- Step 4: Thank You -->
      <div class="step" :class="{
        'active': currentStep === 4
      }" v-if="currentStep === 4">
        <div class="step-header">
          <div class="step-number">4</div>
          <h2>{{ t('thankYouTitle') }}</h2>
          <p>{{ t('thankYouSubtitle') }}</p>
        </div>

        <div class="order-summary">
          <div class="summary-card">
            <div class="summary-header">
              <div class="package-flag">{{ selectedPackage.flag }}</div>
              <div class="package-details">
                <h3>{{ language === 'he' ? selectedPackage.name : selectedPackage.name_en }}</h3>
                <p>{{ language === 'he' ? selectedPackage.description : selectedPackage.description_en }}</p>
              </div>
            </div>

            <div class="summary-content">
              <div class="summary-item">
                <span>{{ t('simNumber') }}</span>
                <span>{{ simNumber }}</span>
              </div>
              <div class="summary-item">
                <span>{{ t('rentalPeriod') }}</span>
                <span>{{ rentalDuration }} {{ language === 'he' ? 'ימים' : 'days' }} ({{ formatDate(rentalStartDate) }} - {{ formatDate(rentalEndDate) }})</span>
              </div>
              <div class="summary-item">
                <span>{{ t('customerName') }}</span>
                <span>{{ customerDetails.firstName }} {{ customerDetails.lastName }}</span>
              </div>
              <div class="summary-item">
                <span>{{ t('endPeriodAction') }}</span>
                <span>{{ endPeriodAction === 'freeze' ? t('freezeLabel') : t('cancelLabel') }}</span>
              </div>
              <div class="summary-item total">
                <span>{{ t('totalToPay') }}</span>
                <span class="price">₪{{ calculatedPrice }}</span>
              </div>
            </div>
          </div>

          <div class="success-message" :class="{ 'pending': paymentStatus === 'pending' || paymentStatus === 'processing' }">
            <p class="activation-status" v-if="paymentStatus">{{ t('status_' + paymentStatus) }}</p>
            <p class="activated-number" v-if="activatedNumber">{{ t('yourNumber') }} <span dir="ltr">{{ activatedNumber }}</span></p>
            <p>{{ t('confirmationEmail') }}</p>
            <p>{{ t('thankYouMessage') }}</p>

            <!-- activation did not complete: contact customer service, subject is set to rentals on the server -->
            <div class="support-box" v-if="paymentStatus === 'pending' || paymentStatus === 'processing'">
              <a class="support-link" v-if="!supportOpen && !supportSent" @click="supportOpen = true">{{ t('supportLink') }}</a>

              <div class="support-form" v-if="supportOpen && !supportSent">
                <label>{{ t('supportLabel') }}</label>
                <textarea v-model="supportMessage" rows="4" :placeholder="t('supportPlaceholder')"></textarea>
                <div class="sim-error" v-if="supportError"><p>{{ supportError }}</p></div>
                <button class="btn-primary" @click="sendSupportTicket" :disabled="supportSending">
                  {{ supportSending ? t('processing') : t('supportSend') }}
                </button>
              </div>

              <p class="support-sent" v-if="supportSent">{{ t('supportSent') }}</p>
            </div>
          </div>
        </div>

        <div class="step-actions" v-if="currentStep === 4">
          <button class="btn-primary" @click="resetForm">{{ t('newOrder') }}</button>
        </div>
      </div>
      </template>
    </div>

  </div>
</template>

<script>
export default {
  name: 'Rental',
  data() {
    return {
      language: 'he',
      translations: {
        he: {
          heroTitle: 'השכרת קווים סלולריים',
          heroSubtitle: 'השכירו קו סלולרי ישראלי עם שיחות בינלאומיות לאנגליה, לאמריקה או לשתיהן',
          step1Title: 'בחירת חבילה ותאריכים',
          step1Subtitle: 'בחרו חבילת שיחות ותאריכי השכרה',
          startDate: 'תאריך התחלה:',
          endDate: 'תאריך סיום:',
          fromDate: 'מתאריך:',
          toDate: 'עד תאריך:',
          rentalDuration: 'משך השכרה:',
          minPeriodNote: 'תקופת השכרה: מינימום שבוע, מקסימום חודש',
          totalToPay: 'סה"כ לתשלום:',
          back: 'חזור',
          continue: 'המשך',
          step2Title: 'פרטי השכרה ופרטים אישיים',
          step2Subtitle: 'מספר SIM, אפשרויות סיום ופרטים ליצירת קשר',
          simLabel: 'מספר סים (8 ספרות אחרונות):',
          endPeriodTitle: 'מה לעשות בסוף תקופת ההשכרה?',
          freezeText: 'להקפיא את הקו לשימוש עתידי',
          freezePrice: '(עלות: ₪10 לחודש)',
          cancelText: 'לבטל את הקו בסוף התקופה',
          firstName: 'שם פרטי',
          lastName: 'שם משפחה',
          email: 'אימייל',
          emailHint: 'חובה - לצורך התחברות למערכת',
          phone: 'טלפון ליצירת קשר',
          phoneHint: 'ליצירת קשר בלבד',
          step3Title: 'סיכום הזמנה',
          step3Subtitle: 'בדקו את הפרטים לפני התשלום',
          simNumber: 'מספר SIM:',
          rentalPeriod: 'תקופת השכרה:',
          customerName: 'שם:',
          endPeriodAction: 'בסוף התקופה:',
          freezeLabel: 'הקפאה',
          cancelLabel: 'ביטול',
          freezeCostAfter: 'עלות הקפאה חודשית:',
          perMonth: 'לחודש',
          processing: 'מעבד...',
          goToPayment: 'מעבר לתשלום',
          thankYouTitle: 'תודה רבה!',
          thankYouSubtitle: 'ההזמנה שלכם התקבלה בהצלחה',
          confirmationEmail: 'הודעת אישור תישלח לאימייל שלכם עם פרטי ההזמנה',
          thankYouMessage: 'תודה שבחרתם ב-BestPhone!',
          newOrder: 'הזמנה חדשה',
          status_activated: 'התשלום בוצע בהצלחה והקו הופעל.',
          status_scheduled: 'התשלום בוצע בהצלחה. הקו יופעל בתאריך תחילת ההשכרה.',
          status_pending: 'התשלום בוצע בהצלחה, אך הפעלת הקו טרם הושלמה. המערכת תנסה שוב באופן אוטומטי.',
          yourNumber: 'מספר הקו שלך:',
          supportLink: 'לפנייה לשירות הלקוחות',
          supportLabel: 'פנייה לשירות הלקוחות בנושא ההשכרה',
          supportPlaceholder: 'כתבו כאן את הפנייה. פרטי ההזמנה יצורפו אוטומטית',
          supportSend: 'שליחת הפנייה',
          supportSent: 'הפנייה נשלחה. נציג שירות יחזור אליכם למייל שהוזן בהזמנה.',
          supportErrShort: 'יש לכתוב לפחות 10 תווים',
          supportErrSend: 'לא ניתן היה לשלוח את הפנייה. יש לנסות שוב',
          status_processing: 'התשלום התקבל ומעובד. הקו יופעל לאחר אישור התשלום.',
          emailRequired: 'נא להזין כתובת אימייל תקינה',
          minDaysError: 'תקופת ההשכרה המינימלית היא שבוע (7 ימים)',
          maxDaysError: 'תקופת ההשכרה המקסימלית היא חודש (30 ימים)',
          endBeforeStartError: 'תאריך סיום חייב להיות אחרי תאריך התחלה',
          selectStartError: 'נא לבחור תאריך התחלה',
          selectEndError: 'נא לבחור תאריך סיום',
          simError: 'יש להכניס 8 ספרות לפחות',
          simTakenError: 'מספר הסים הזה כבר משויך לקו פעיל. יש לבדוק את המספר או להשתמש בסים אחר',
          simInvalidError: 'מספר הסים לא נמצא. יש לבדוק שהוזנו נכון 8 הספרות האחרונות',
          payErrPlan: 'החבילה שנבחרה לא נמצאה. יש לחזור ולבחור חבילה מחדש',
          payErrPrice: 'לא ניתן לחשב מחיר לחבילה ולתקופה שנבחרו. יש לבדוק את תאריכי ההשכרה',
          payErrNetwork: 'לא התקבלה תשובה מהשרת. יש לבדוק את החיבור לאינטרנט ולנסות שוב',
          payErrUnknown: 'לא ניתן היה ליצור את ההזמנה. יש לנסות שוב'
        },
        en: {
          heroTitle: 'Cellular Line Rental',
          heroSubtitle: 'Rent an Israeli cellular line with international calls to England, America, or both',
          step1Title: 'Choose Plan & Dates',
          step1Subtitle: 'Select a calling plan and rental dates',
          startDate: 'Start date:',
          endDate: 'End date:',
          fromDate: 'From:',
          toDate: 'Until:',
          rentalDuration: 'Rental duration:',
          minPeriodNote: 'Rental period: minimum 1 week, maximum 1 month',
          totalToPay: 'Total to pay:',
          back: 'Back',
          continue: 'Continue',
          step2Title: 'Rental & Personal Details',
          step2Subtitle: 'SIM number, end-of-period options, and contact info',
          simLabel: 'SIM number (last 8 digits):',
          endPeriodTitle: 'What to do at the end of the rental period?',
          freezeText: 'Freeze the line for future use',
          freezePrice: '(Cost: ₪10 / month)',
          cancelText: 'Cancel the line at the end of the period',
          firstName: 'First name',
          lastName: 'Last name',
          email: 'Email',
          emailHint: 'Required - for system login',
          phone: 'Contact phone',
          phoneHint: 'For contact purposes only',
          step3Title: 'Order Summary',
          step3Subtitle: 'Review details before payment',
          simNumber: 'SIM number:',
          rentalPeriod: 'Rental period:',
          customerName: 'Name:',
          endPeriodAction: 'End of period:',
          freezeLabel: 'Freeze',
          cancelLabel: 'Cancel',
          freezeCostAfter: 'Monthly freeze cost:',
          perMonth: '/ month',
          processing: 'Processing...',
          goToPayment: 'Go to payment',
          thankYouTitle: 'Thank you!',
          thankYouSubtitle: 'Your order has been received successfully',
          confirmationEmail: 'A confirmation email with your order details will be sent to your email',
          thankYouMessage: 'Thank you for choosing BestPhone!',
          newOrder: 'New order',
          status_activated: 'Payment completed and your line is active.',
          status_scheduled: 'Payment completed. Your line will be activated on the rental start date.',
          status_pending: 'Payment completed, but line activation is not finished yet. The system will retry automatically.',
          yourNumber: 'Your line number:',
          supportLink: 'Contact customer service',
          supportLabel: 'Contact customer service about this rental',
          supportPlaceholder: 'Write your message here. The order details are attached automatically',
          supportSend: 'Send',
          supportSent: 'Your message was sent. A representative will reply to the email entered in the order.',
          supportErrShort: 'Please write at least 10 characters',
          supportErrSend: 'The message could not be sent. Please try again',
          status_processing: 'Payment received and is being processed. Your line will be activated once it is approved.',
          emailRequired: 'Please enter a valid email address',
          minDaysError: 'Minimum rental period is one week (7 days)',
          maxDaysError: 'Maximum rental period is one month (30 days)',
          endBeforeStartError: 'End date must be after start date',
          selectStartError: 'Please select a start date',
          selectEndError: 'Please select an end date',
          simError: 'Please enter at least 8 digits',
          simTakenError: 'This SIM number is already assigned to an active line. Check the number or use a different SIM',
          simInvalidError: 'SIM number not found. Check that the last 8 digits were entered correctly',
          payErrPlan: 'The selected plan was not found. Go back and choose a plan again',
          payErrPrice: 'A price could not be calculated for the selected plan and period. Check the rental dates',
          payErrNetwork: 'No response from the server. Check your internet connection and try again',
          payErrUnknown: 'The order could not be created. Please try again'
        }
      },
      currentStep: 1,
      selectedPackage: null,
      rentalStartDate: null,
      rentalEndDate: null,
      today: new Date().toISOString().slice(0, 10),
      dateError: '',
      emailError: '',
      customerDetails: {
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
      },
      isCompleted: false,
      isProcessing: false,
      paymentId: 0,
      paymentUrl: '',
      paymentStatus: '',
      paymentError: '',
      activatedNumber: '',
      supportOpen: false,
      supportMessage: '',
      supportSending: false,
      supportSent: false,
      supportError: '',
      packages: [],
      simNumber: '',
      simNumberError: '',
      endPeriodAction: '',
      canEditStep1: true,
      canEditStep2: true
    }
  },
  created() {
    this.loadPlans();
    window.addEventListener('message', this.onPaymentMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onPaymentMessage);
  },
  computed: {
    minEndDate() {
      if (!this.rentalStartDate) return this.today;
      const start = new Date(this.rentalStartDate);
      start.setDate(start.getDate() + 6);
      return start.toISOString().slice(0, 10);
    },
    rentalDuration() {
      if (!this.rentalStartDate || !this.rentalEndDate) return 0;
      const start = new Date(this.rentalStartDate);
      const end = new Date(this.rentalEndDate);
      return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    },
    calculatedPrice() {
      if (!this.selectedPackage || !this.rentalStartDate || !this.rentalEndDate || this.dateError) return 0;

      const duration = this.rentalDuration;
      const periods = this.selectedPackage.periods;

      for (let i = 0; i < periods.length; i++) {
        if (duration <= periods[i].duration) {
          return periods[i].price;
        }
      }
      return periods[periods.length - 1].price;
    },
    pricingTiers() {
      if (!this.selectedPackage) return [];
      const isHe = this.language === 'he';
      return this.selectedPackage.periods.map(p => {
        let label;
        if (p.duration === 7) {
          label = isHe ? 'שבוע' : 'Week';
        } else if (p.duration === 14) {
          label = isHe ? 'עד שבועיים' : 'Up to 2 weeks';
        } else if (p.duration === 30) {
          label = isHe ? 'עד חודש' : 'Up to 1 month';
        }
        return { label, price: p.price };
      });
    },
    freezeCost() {
      return 10;
    },
    isCustomerFormValid() {
      return this.customerDetails.firstName &&
             this.customerDetails.lastName &&
             this.customerDetails.email &&
             this.isValidEmail(this.customerDetails.email) &&
             this.customerDetails.phone;
    }
  },
  watch: {
    rentalStartDate() {
      this.calculateRentalPeriod();
    },
    rentalEndDate() {
      this.calculateRentalPeriod();
    }
  },
  methods: {
    t(key) {
      const dict = this.translations[this.language] || this.translations.he;
      return dict[key] || key;
    },
    setLanguage(lang) {
      this.language = ['he', 'en'].includes(lang) ? lang : 'he';
    },
    loadPlans() {
      this.api({ action: 'rentals_plans' }, (res) => {
        const items = res && res.data && res.data.items ? res.data.items : [];
        this.packages = items.map((p) => {
          const periods = [];
          if (p.week_price != null && p.week_price !== '')
            periods.push({ id: 'week1', duration: 7, price: Number(p.week_price) });
          if (p.two_weeks_price != null && p.two_weeks_price !== '')
            periods.push({ id: 'week2', duration: 14, price: Number(p.two_weeks_price) });
          if (p.month_price != null && p.month_price !== '')
            periods.push({ id: 'month', duration: 30, price: Number(p.month_price) });
          return {
            id: p.id,
            name: p.name || p.title || '',
            name_en: p.name_en || p.name || p.title || '',
            flag: p.flag || '',
            description: p.description || '',
            description_en: p.description_en || p.description || '',
            pack_id: p.pack_id || p.id,
            periods
          };
        });
      });
    },
    selectPackage(pkg) {
      if (this.selectedPackage && this.selectedPackage.id === pkg.id) return;
      this.selectedPackage = pkg;
      this.rentalStartDate = null;
      this.rentalEndDate = null;
      this.dateError = '';
      this.simNumber = '';
      this.simNumberError = '';
      this.endPeriodAction = '';
    },
    validateSimNumber() {
      this.simNumberError = '';

      if (!this.simNumber) {
        return;
      }

      const cleanNumber = this.simNumber.replace(/[\s\-\(\)]/g, '');

      if (!cleanNumber.match(/^\d{8,20}$/)) {
        this.simNumberError = this.t('simError');
      }
    },
    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    validateEmail() {
      this.emailError = '';
      if (this.customerDetails.email && !this.isValidEmail(this.customerDetails.email)) {
        this.emailError = this.t('emailRequired');
      }
    },
    nextStep() {
      if (this.currentStep < 4) {
        this.currentStep++;
        this.resetEditIcons();
      }
    },
    resetEditIcons() {
      this.canEditStep1 = false;
      this.canEditStep2 = false;
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.canEditStep1 = true;
        this.canEditStep2 = true;
      }
    },
    goToStep(step) {
      this.currentStep = step;
      this.canEditStep1 = true;
      this.canEditStep2 = true;
    },
    processPayment() {
      this.isProcessing = true;

      const orderData = {
        package_type: this.selectedPackage.id,
        pack_id: this.selectedPackage.pack_id,
        sim_number: this.simNumber.replace(/[\s\-\(\)]/g, ''),
        start_date: this.rentalStartDate,
        end_date: this.rentalEndDate,
        want_sms: false,
        end_period_action: this.endPeriodAction,
        customer: this.customerDetails
      };

      this.paymentError = '';

      // shown on the page, above the payment button
      const fail = (reason) => {
        this.isProcessing = false;
        this.paymentError = reason;
      };

      this.api(
        { action: 'create_order_rental', data: orderData },
        (res) => {
          const r = res && res.data ? res.data : res;
          if (r && r.payment_link) {
            this.paymentId = r.payment_id;
            this.paymentUrl = r.payment_link;
            this.isProcessing = false;
          } else if (r && (r.error === 'sim_taken' || r.error === 'sim_invalid')) {
            // the SIM was rejected before any order or payment was created - back to the SIM field
            this.isProcessing = false;
            this.goToStep(2);
            this.simNumberError = this.t(r.error === 'sim_taken' ? 'simTakenError' : 'simInvalidError');
          } else if (r && r.error === 'plan_not_found') {
            fail(this.t('payErrPlan'));
          } else if (r && r.error === 'price_error') {
            fail(this.t('payErrPrice'));
          } else {
            fail(this.t('payErrUnknown') + (r && r.error ? ' (' + r.error + ')' : ''));
          }
        },
        (err) => {
          const code = err && err.response && err.response.status ? err.response.status : '';
          fail(this.t('payErrNetwork') + (code ? ' (' + code + ')' : ''));
        }
      );
    },
    // payments/rental_success, loaded inside the payment iframe once the charge is done
    onPaymentMessage(e) {
      const frame = this.$refs.paymentFrame;
      const d = e.data;

      if (!frame || e.source !== frame.contentWindow) return;
      if (!d || d.source !== 'bestphone_rental' || Number(d.payment_id) !== Number(this.paymentId)) return;

      this.paymentStatus = d.status;
      this.activatedNumber = d.number || '';
      this.paymentUrl = '';
      this.currentStep = 4;
    },
    sendSupportTicket() {
      this.supportError = '';

      if (this.supportMessage.trim().length < 10) {
        this.supportError = this.t('supportErrShort');
        return;
      }

      this.supportSending = true;

      const fail = () => {
        this.supportSending = false;
        this.supportError = this.t('supportErrSend');
      };

      this.api(
        { action: 'rental_support_ticket', data: { payment_id: this.paymentId, phone: this.customerDetails.phone, message: this.supportMessage } },
        (res) => {
          const r = res && res.data ? res.data : res;
          if (r && r.success) {
            this.supportSending = false;
            this.supportSent = true;
          } else if (r && r.error === 'invalid_message') {
            this.supportSending = false;
            this.supportError = this.t('supportErrShort');
          } else {
            fail();
          }
        },
        fail
      );
    },
    calculateRentalPeriod() {
      this.dateError = '';

      if (!this.rentalStartDate && !this.rentalEndDate) {
        return;
      }

      if (!this.rentalStartDate) {
        this.dateError = this.t('selectStartError');
        return;
      }
      if (!this.rentalEndDate) {
        this.dateError = this.t('selectEndError');
        return;
      }

      const start = new Date(this.rentalStartDate);
      const end = new Date(this.rentalEndDate);

      if (end < start) {
        this.dateError = this.t('endBeforeStartError');
        return;
      }

      const duration = this.rentalDuration;

      if (duration < 7) {
        this.dateError = this.t('minDaysError');
        return;
      }

      if (duration > 30) {
        this.dateError = this.t('maxDaysError');
        return;
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('he-IL', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
      });
    },
    resetForm() {
      this.currentStep = 1;
      this.selectedPackage = null;
      this.simNumber = '';
      this.simNumberError = '';
      this.endPeriodAction = '';
      this.rentalStartDate = null;
      this.rentalEndDate = null;
      this.dateError = '';
      this.emailError = '';
      this.customerDetails = {
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
      };
      this.isCompleted = false;
      this.canEditStep1 = true;
      this.canEditStep2 = true;
      this.paymentId = 0;
      this.paymentUrl = '';
      this.paymentStatus = '';
      this.paymentError = '';
      this.activatedNumber = '';
      this.supportOpen = false;
      this.supportMessage = '';
      this.supportSending = false;
      this.supportSent = false;
      this.supportError = '';
    }
  }
}
</script>

<style scoped>
.rental-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f0ff 0%, #eef2ff 50%, #f0f7ff 100%);
  font-family: 'Leon-Regular', sans-serif;
}

/* ── Hero ── */
.hero-section {
  background: linear-gradient(135deg, #7c3aed 0%, #8424e3 40%, #a855f7 100%);
  padding: 52px 20px 44px;
  text-align: center;
  color: #fff;
  position: relative;
  overflow: hidden;
}

.hero-section::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 30% 20%, rgba(255,255,255,0.12) 0%, transparent 60%),
              radial-gradient(circle at 80% 80%, rgba(255,255,255,0.08) 0%, transparent 50%);
  pointer-events: none;
}

.hero-content {
  position: relative;
}

.hero-title {
  font-size: 2.3rem;
  font-weight: 800;
  margin-bottom: 10px;
  text-shadow: 0 2px 12px rgba(0,0,0,0.15);
}

.hero-subtitle {
  font-size: 1.05rem;
  color: rgba(255,255,255,0.88);
  max-width: 600px;
  margin: 0 auto;
}

/* ── Form Container ── */
.rental-form {
  max-width: 820px;
  margin: -24px auto 0;
  padding: 0 16px 40px;
  position: relative;
}

/* ── Language Selector ── */
.lang-selector {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-bottom: 20px;
  padding-top: 8px;
}

.lang-btn {
  padding: 7px 16px;
  border-radius: 20px;
  border: 1.5px solid rgba(132,36,227,0.2);
  background: rgba(255,255,255,0.85);
  backdrop-filter: blur(8px);
  cursor: pointer;
  font-weight: 700;
  font-size: 0.88rem;
  color: #555;
  transition: all 0.2s ease;
}

.lang-btn:hover {
  border-color: var(--color_primary, #8424e3);
  color: var(--color_primary, #8424e3);
}

.lang-btn.active {
  color: #fff;
  background: var(--color_primary, #8424e3);
  border-color: var(--color_primary, #8424e3);
  box-shadow: 0 2px 10px rgba(132,36,227,0.3);
}

/* ── Progress Bar ── */
.progress-bar {
  max-width: 340px;
  margin: 0 auto 28px;
  position: relative;
  padding: 0 20px;
}

.progress-track {
  position: absolute;
  top: 14px;
  left: 40px;
  right: 40px;
  height: 3px;
  background: #e5e0f0;
  border-radius: 3px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #8424e3, #a855f7);
  border-radius: 3px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.progress-steps {
  display: flex;
  justify-content: space-between;
  position: relative;
}

.progress-dot {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #fff;
  border: 2.5px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 0.78rem;
  color: #aaa;
  transition: all 0.3s ease;
}

.progress-dot.active {
  border-color: var(--color_primary, #8424e3);
  color: var(--color_primary, #8424e3);
  background: #faf5ff;
  box-shadow: 0 0 0 4px rgba(132,36,227,0.1);
}

.progress-dot.done {
  background: var(--color_primary, #8424e3);
  border-color: var(--color_primary, #8424e3);
  color: #fff;
  box-shadow: 0 2px 8px rgba(132,36,227,0.3);
}

/* ── Steps ── */
.step {
  background: #fff;
  border-radius: 16px;
  padding: 32px 28px;
  box-shadow: 0 4px 24px rgba(132,36,227,0.06), 0 1px 4px rgba(0,0,0,0.04);
  margin-bottom: 20px;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  border: 1px solid rgba(132,36,227,0.06);
}

.step.completed {
  pointer-events: none;
  opacity: 0.7;
  transform: scale(0.98);
}

.step.completed .step-header::after {
  content: '\2713';
  position: absolute;
  top: 12px;
  right: 16px;
  color: #22c55e;
  font-weight: 800;
  font-size: 18px;
  background: #ecfdf5;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step.active {
  box-shadow: 0 8px 32px rgba(132,36,227,0.1), 0 2px 8px rgba(0,0,0,0.04);
}

.step-header {
  text-align: center;
  margin-bottom: 28px;
  position: relative;
}

.step-number {
  display: none;
}

.step-header h2 {
  font-size: 1.45rem;
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 6px;
  margin-top: 0;
}

.step-header p {
  font-size: 0.92rem;
  color: #888;
}

.edit-icon {
  position: absolute;
  top: 0;
  right: 0;
  color: var(--color_primary, #8424e3);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: 0.5;
  pointer-events: auto;
  border-radius: 8px;
}

.edit-icon:hover {
  opacity: 1;
  background: #faf5ff;
}

.step.completed:hover .edit-icon {
  opacity: 1;
}

/* ── Package Cards ── */
.package-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 24px;
}

.package-card {
  background: #fff;
  border: 2px solid #f0ecf5;
  border-radius: 14px;
  padding: 22px 18px;
  text-align: center;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.package-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: transparent;
  transition: background 0.25s ease;
}

.package-card:hover {
  border-color: #d4c5f0;
  box-shadow: 0 6px 20px rgba(132,36,227,0.08);
  transform: translateY(-2px);
}

.package-card.active {
  border-color: var(--color_primary, #8424e3);
  background: linear-gradient(180deg, #faf5ff 0%, #fff 100%);
  box-shadow: 0 6px 24px rgba(132,36,227,0.14);
  transform: translateY(-2px);
}

.package-card.active::before {
  background: linear-gradient(90deg, #8424e3, #a855f7);
}

.package-icon {
  font-size: 2.4rem;
  margin-bottom: 10px;
}

.package-card h3 {
  font-size: 1.1rem;
  font-weight: 800;
  margin-bottom: 6px;
  color: #1a1a2e;
}

.package-card p {
  font-size: 0.85rem;
  color: #888;
  margin-bottom: 10px;
  line-height: 1.4;
}

.package-price-range {
  font-size: 0.88rem;
  color: var(--color_primary, #8424e3);
  font-weight: 700;
  background: #faf5ff;
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
}

/* ── Pricing Table ── */
.period-selection {
  max-width: 420px;
  margin: 0 auto 24px;
}

.pricing-table {
  background: linear-gradient(135deg, #faf5ff 0%, #f5f0ff 100%);
  border: 1px solid #e9e0f5;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pricing-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 4px;
  border-bottom: 1px solid rgba(132,36,227,0.08);
}

.pricing-row:last-child {
  border-bottom: none;
}

.tier-label {
  font-weight: 600;
  color: #555;
  font-size: 0.93rem;
}

.tier-price {
  font-weight: 800;
  color: var(--color_primary, #8424e3);
  font-size: 1.05rem;
}

/* ── Date Inputs ── */
.date-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 8px;
}

.date-input-group {
  margin-bottom: 10px;
}

.date-input-group label {
  display: block;
  font-weight: 700;
  margin-bottom: 6px;
  color: #333;
  font-size: 0.9rem;
}

.date-input {
  width: 100%;
  padding: 12px;
  border: 1.5px solid #e5e0f0;
  border-radius: 10px;
  font-size: 1rem;
  text-align: center;
  transition: all 0.2s ease;
  background: #fafafe;
}

.date-input:focus {
  outline: none;
  border-color: var(--color_primary, #8424e3);
  box-shadow: 0 0 0 3px rgba(132,36,227,0.08);
  background: #fff;
}

.date-input.error {
  border-color: #dc2626;
  box-shadow: 0 0 0 3px rgba(220,38,38,0.08);
}

.min-period-note {
  text-align: center;
  margin-bottom: 12px;
}

.min-period-note p {
  font-size: 0.82rem;
  color: #aaa;
  font-style: italic;
}

/* ── Date Error ── */
.date-error {
  background: linear-gradient(135deg, #fef2f2, #fff5f5);
  color: #dc2626;
  padding: 10px 14px;
  border-radius: 10px;
  margin: 10px 0;
  font-size: 0.88rem;
  border: 1px solid #fecaca;
}

.date-error p {
  margin: 0;
}

/* ── Rental Period Display ── */
.rental-period-display {
  background: linear-gradient(135deg, #faf5ff 0%, #f0f7ff 100%);
  border: 1px solid #e9e0f5;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 14px;
}

.period-info {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 12px;
}

.period-dates {
  display: flex;
  align-items: center;
  gap: 6px;
}

.date-label,
.duration-label {
  font-weight: 700;
  color: #555;
  font-size: 0.88rem;
}

.date-value,
.duration-value {
  font-weight: 800;
  color: var(--color_primary, #8424e3);
}

/* ── Price Display ── */
.price-display {
  background: linear-gradient(135deg, #8424e3 0%, #a855f7 100%);
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 16px;
}

.price-display .price-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.price-display .price-item span:first-child {
  color: rgba(255,255,255,0.88);
  font-weight: 600;
  font-size: 1rem;
}

.price-display .price {
  color: #fff;
  font-size: 1.4rem;
  font-weight: 800;
}

/* ── Combined Details Section ── */
.combined-details-section {
  max-width: 520px;
  margin: 0 auto 24px;
}

.section-divider {
  border: none;
  height: 1px;
  background: linear-gradient(90deg, transparent, #e5e0f0, transparent);
  margin: 28px 0;
}

.sim-input-section {
  max-width: 500px;
  margin: 0 auto 24px;
}

.sim-input-form {
  margin-top: 16px;
}

.sim-input {
  width: 100%;
  padding: 12px;
  border: 1.5px solid #e5e0f0;
  border-radius: 10px;
  font-size: 1.1rem;
  transition: all 0.2s ease;
  text-align: center;
  background: #fafafe;
  letter-spacing: 2px;
  font-weight: 600;
}

.sim-input:focus {
  outline: none;
  border-color: var(--color_primary, #8424e3);
  box-shadow: 0 0 0 3px rgba(132,36,227,0.08);
  background: #fff;
}

.sim-input.error {
  border-color: #dc2626;
}

.sim-error {
  background: linear-gradient(135deg, #fef2f2, #fff5f5);
  color: #dc2626;
  padding: 10px 14px;
  border-radius: 10px;
  margin: 10px 0;
  font-size: 0.88rem;
  border: 1px solid #fecaca;
}

.sim-error p {
  margin: 0;
}

/* ── End Period Option ── */
.end-period-option {
  margin-top: 24px;
  padding: 20px;
  background: linear-gradient(135deg, #fafafe, #f8f5ff);
  border-radius: 12px;
  border: 1px solid #ede8f5;
}

.option-title {
  display: block;
  font-weight: 700;
  margin-bottom: 14px;
  color: #333;
  font-size: 0.95rem;
}

.radio-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.radio-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 0.93rem;
  line-height: 1.5;
  padding: 14px;
  border: 1.5px solid #ede8f5;
  border-radius: 10px;
  background: #fff;
  transition: all 0.2s ease;
}

.radio-label:hover {
  border-color: #c9b8e8;
  background: #fdfbff;
}

.radio-input {
  margin: 0;
  margin-top: 3px;
  width: 18px;
  height: 18px;
  accent-color: var(--color_primary, #8424e3);
  cursor: pointer;
  flex-shrink: 0;
}

.radio-text {
  flex: 1;
  color: #444;
}

.freeze-price {
  color: var(--color_primary, #8424e3);
  font-weight: 600;
  font-size: 0.88rem;
}

/* ── Customer Form ── */
.customer-form {
  max-width: 520px;
  margin: 0 auto 24px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}

.form-group {
  margin-bottom: 14px;
}

.form-group label {
  display: block;
  font-weight: 700;
  margin-bottom: 6px;
  color: #333;
  font-size: 0.9rem;
}

.form-group input {
  width: 100%;
  padding: 12px;
  border: 1.5px solid #e5e0f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background: #fafafe;
}

.form-group input:focus {
  outline: none;
  border-color: var(--color_primary, #8424e3);
  box-shadow: 0 0 0 3px rgba(132,36,227,0.08);
  background: #fff;
}

.form-group input.error {
  border-color: #dc2626;
}

.required {
  color: #dc2626;
  font-weight: 700;
}

.field-hint {
  display: block;
  font-size: 0.78rem;
  color: #aaa;
  margin-top: 5px;
}

.field-error {
  background: linear-gradient(135deg, #fef2f2, #fff5f5);
  color: #dc2626;
  padding: 8px 12px;
  border-radius: 8px;
  margin-top: 6px;
  font-size: 0.83rem;
  border: 1px solid #fecaca;
}

.field-error p {
  margin: 0;
}

/* ── Summary Cards ── */
.package-summary {
  max-width: 500px;
  margin: 0 auto 24px;
}

.summary-card {
  background: #fff;
  border: 1px solid #ede8f5;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(132,36,227,0.04);
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
  padding-bottom: 18px;
  border-bottom: 1px solid #f0ecf5;
}

.package-flag {
  font-size: 2.2rem;
}

.package-details h3 {
  font-size: 1.15rem;
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 4px;
}

.package-details p {
  color: #888;
  font-size: 0.88rem;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  margin-bottom: 0;
  border-bottom: 1px solid #f5f2fa;
  color: #555;
  font-size: 0.93rem;
}

.summary-item.total {
  border-bottom: none;
  border-top: 2px solid #ede8f5;
  padding-top: 14px;
  margin-top: 8px;
  font-weight: 800;
  font-size: 1.15rem;
  color: #1a1a2e;
}

.summary-item.freeze-info {
  color: #888;
  font-style: italic;
  font-size: 0.88rem;
}

.price {
  font-weight: 800;
  color: var(--color_primary, #8424e3);
}

/* ── Order Summary (Thank You) ── */
.order-summary {
  border-radius: 14px;
  padding: 0;
  margin: 0 0 20px;
}

.success-message {
  text-align: center;
  margin-top: 20px;
  padding: 24px;
  background: linear-gradient(135deg, #ecfdf5, #f0fdf4);
  border-radius: 12px;
  border: 1px solid #86efac;
}

.success-message p {
  margin: 8px 0;
  color: #15803d;
  font-weight: 500;
  font-size: 0.95rem;
}

.success-message .activation-status {
  font-size: 1.1rem;
  font-weight: 700;
}

.success-message.pending {
  background: linear-gradient(135deg, #fffbeb, #fefce8);
  border-color: #fcd34d;
}

.success-message.pending p {
  color: #92400e;
}

.success-message .activated-number {
  font-size: 1.25rem;
  font-weight: 700;
}

.support-box {
  margin-top: 16px;
}

.support-link {
  color: #6d2ed3;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

.support-form {
  text-align: right;
}

.support-form label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #291f40;
}

.support-form textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border: 1px solid #d7c6f7;
  border-radius: 10px;
  font-family: inherit;
  font-size: 0.95rem;
  resize: vertical;
  margin-bottom: 12px;
}

.success-message .support-sent {
  color: #15803d;
  font-weight: 700;
}

/* ── Payment iframe ── */
.payment-frame {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 24px rgba(132,36,227,0.06), 0 1px 4px rgba(0,0,0,0.04);
  border: 1px solid rgba(132,36,227,0.06);
  text-align: center;
}

.payment-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1a1a2e;
  margin: 0 0 16px;
}

.payment-frame iframe {
  border: none;
  width: 100%;
  height: 600px;
  max-height: 80vh;
  overflow-y: scroll;
}

.payment-frame .go-back {
  display: inline-block;
  margin-top: 16px;
  color: var(--color_primary, #8424e3);
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
}

/* ── Buttons ── */
.step-actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.btn-primary, .btn-secondary {
  padding: 13px 28px;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 130px;
}

.btn-primary {
  background: linear-gradient(135deg, #8424e3 0%, #9b4de8 100%);
  color: #fff;
  border: none;
  box-shadow: 0 4px 14px rgba(132,36,227,0.25);
}

.btn-primary:hover:not(:disabled) {
  box-shadow: 0 6px 20px rgba(132,36,227,0.35);
  transform: translateY(-1px);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-secondary {
  background: #fff;
  color: #555;
  border: 1.5px solid #e5e0f0;
}

.btn-secondary:hover {
  background: #faf5ff;
  border-color: #d4c5f0;
  color: var(--color_primary, #8424e3);
}

/* ── Mobile Responsive ── */
@media (max-width: 768px) {
  .hero-title {
    font-size: 1.8rem;
  }

  .hero-section {
    padding: 40px 16px 36px;
  }

  .step {
    padding: 24px 20px;
    border-radius: 14px;
  }

  .package-options {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .package-card {
    padding: 18px 16px;
  }

  .sim-input-section {
    max-width: 90%;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .date-inputs {
    grid-template-columns: 1fr;
  }

  .step-actions {
    flex-direction: column;
  }

  .btn-primary, .btn-secondary {
    width: 100%;
  }

  .progress-bar {
    max-width: 260px;
  }
}
</style>
